// Frontend fetch wrapper — Yapay zekâ açıklama uç noktası için.
import { getAccessToken } from "@/supabase/client";
// Anahtar HİÇBİR ŞEKİLDE istemci tarafına taşınmaz; sadece /api/ai/explain
// sunucu uç noktasına yönlendirir. Cloudflare Pages Function cevap verir.

export type AiExplainResponse =
  | { ok: true; model: string; text: string }
  | { ok: false; code: string; message: string };

export async function explainWithAI(
  prompt: string,
  companyId: string,
  signal?: AbortSignal,
): Promise<AiExplainResponse> {
  if (!prompt?.trim() || !companyId) {
    return {
      ok: false,
      code: "invalid_request",
      message: "prompt boş olamaz.",
    };
  }

  const accessToken = await getAccessToken();
  if (!accessToken) {
    return {
      ok: false,
      code: "unauthorized",
      message: "Yapay zekâ analizi için oturum açmanız gerekiyor. Lütfen yeniden giriş yapın.",
    };
  }

  let res: Response;
  try {
    res = await fetch("/api/ai/explain", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ prompt, companyId }),
      signal,
    });
  } catch (e) {
    return {
      ok: false,
      code: "network_error",
      message:
        e instanceof Error ? e.message : "Ağ hatası, sunucuya ulaşılamadı.",
    };
  }

  let data: any;
  try {
    data = await res.json();
  } catch {
    const contentType = res.headers.get("content-type") || "belirtilmedi";
    return {
      ok: false,
      code: "invalid_response",
      message: `Sunucudan JSON yanıtı alınamadı (HTTP ${res.status}; Content-Type: ${contentType}). Cloudflare Pages API dağıtımı veya /api/ai/explain yönlendirmesi kontrol edilmeli.`,
    };
  }

  if (!res.ok) {
    return {
      ok: false,
      code: data?.error?.code ?? "unknown_error",
      message: data?.error?.message ?? `HTTP ${res.status}: bilinmeyen bir hata oluştu.`,
    };
  }

  if (typeof data?.text !== "string" || !data.text.trim()) {
    return {
      ok: false,
      code: "invalid_response_shape",
      message: "Sunucu yanıtı geldi ancak beklenen yapay zekâ yanıtı bulunamadı.",
    };
  }

  return {
    ok: true,
    model: typeof data.model === "string" ? data.model : "Cloudflare Workers AI",
    text: data.text,
  };
}

export async function transcribeWithAI(
  audio: Blob,
  companyId: string,
): Promise<{ ok: true; text: string } | { ok: false; message: string }> {
  if (!audio.size || audio.size > 8 * 1024 * 1024 || !companyId) {
    return { ok: false, message: "Ses kaydı boş veya 8 MB sınırını aşıyor." };
  }
  const accessToken = await getAccessToken();
  if (!accessToken) return { ok: false, message: "Sesli soru için oturum açmanız gerekiyor." };
  const form = new FormData();
  form.append("audio", audio, "question.webm");
  form.append("companyId", companyId);
  try {
    const response = await fetch("/api/ai/transcribe", {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
      body: form,
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) return { ok: false, message: data?.error?.message ?? "Ses yazıya çevrilemedi." };
    if (typeof data?.text !== "string" || !data.text.trim()) return { ok: false, message: "Konuşma algılanamadı. Tekrar deneyin." };
    return { ok: true, text: data.text };
  } catch {
    return { ok: false, message: "Ses yazıya çevrilirken bağlantı hatası oluştu." };
  }
}

export async function auditCompanyDataAfterChange(
  companyId: string,
  change: "Nebim satış aktarımı" | "Nebim stok aktarımı" | "Beşiktaş fatura taslağı",
): Promise<AiExplainResponse> {
  const prompt = [
    `OTOMATİK VERİ DENETİMİ: Yeni bir ${change} kaydedildi. Şirketin mevcut Nebim satışları, stok snapshot'ı, gerçek Beşiktaş faturaları (taslaklar dahil), ürün kataloğu ve onaylı ürün eşleşmelerini birlikte denetle.`,
    "Önce veri kapsamını ve kaynak tarihlerini doğrula. Aynı mağaza, dönem ve veri türünü karşılaştır; farklı tarihli snapshot'ları veya farklı satır/adet ölçülerini doğrudan kıyaslama.",
    "Maliyet keşfi için özellikle satışta olup maliyeti eksik SKU’ları bul. Bu SKU’ları ürün kataloğu, onaylı Nebim eşleşmeleri, tedarikçi fatura satırları ve Nebim envanter raporundaki bayi_purchase_price alanıyla çapraz karşılaştır. Ürün kodu ve model kodunu birincil kimlik kanıtı kabul et; ürün açıklamasını normalize ederek karşılaştır; renk/beden farklılıklarını ana ürün maliyeti eşleşmesinin önünde engel yapma. Miktar ve birim fiyatı destekleyici kontrol olarak kullan, tek başına ürün kimliği sayma. Her öneride kaynak belge/tarih, SKU, ürün adı, miktar, birim fiyat, KDV oranı, KDV dahil/hariç durumu ve eşleşme güvenini yaz. Bayi alış fiyatının KDV niteliği doğrulanmadıysa bunu net maliyet gibi kullanma. Fatura satırıyla çelişen fiyatı otomatik seçme; farkı göster. Sarf malzemeleri ürün stok maliyetinden ayrı tut. Ayrıca mükerrer dosya/fatura; satış SKU'sunun katalogda bulunmaması; fatura kaleminin eşleşmemesi veya birden fazla adaya uyması; miktar/birim maliyet/toplam çarpım tutarsızlığı; fatura ara toplamı+KDV-genel toplam farkı; satır KDV toplamı farkı; stokta maliyet eksikliği; negatif veya olağandışı miktar; taslak faturanın stok/cari hareketi oluşturmuş görünmesi; aynı hareketin iki kez işlenmesi.",
    "Önem derecesini KRİTİK / YÜKSEK / ORTA / BİLGİ olarak sınıflandır. Her bulguda kaynak, tarih, etkilenen kayıt/ürün, kanıtlanan değer ve beklenen değer yaz. Kaynakta bulunmayan rakamı üretme.",
    "KESİNLİKLE veritabanına yazma, eşleşme onaylama, stok/cari hareketi oluşturma veya kayıt silme. Bu denetim salt okunurdur. Kesin eşleşme değilse otomatik düzeltildi deme; güvenli öneri sun ve kullanıcı onayı gerektiğini belirt.",
    "Yanıtı kısa bir denetim raporu olarak ver: Sonuç (temiz / uyarı / işlem gerekli), taranan kapsam, bulgular, güvenli otomatik düzeltmeye uygun olanlar, kullanıcı onayı gerekenler ve sonraki adım. Hiç sorun yoksa hangi kontrollerin gerçekten yapıldığını açıkça listele."
  ].join("\\n");
  return explainWithAI(prompt, companyId);
}

export type AiProductMatchInput = {
  sourceId: string;
  code?: string | null;
  name?: string | null;
  quantity?: number | null;
  unitCost?: number | null;
};

export type AiProductMatchProposal = {
  sourceId: string;
  productId: string | null;
  confidence: number;
  reason: string;
  reviewRequired: boolean;
  autoMatch: boolean;
  candidates: Array<{ id: string; code: string; name: string | null; rank: number }>;
};

export async function matchProductsWithAI(
  items: AiProductMatchInput[],
  companyId: string,
): Promise<{ ok: true; model: string; matches: AiProductMatchProposal[]; unresolved: string[] } | { ok: false; message: string }> {
  if (!companyId || !items.length || items.length > 40) {
    return { ok: false, message: "AI eşleştirmesi için şirket ve 1-40 arası ürün satırı gerekli." };
  }
  const accessToken = await getAccessToken();
  if (!accessToken) return { ok: false, message: "AI eşleştirmesi için oturum açmanız gerekiyor." };
  try {
    const response = await fetch("/api/ai/match-products", {
      method: "POST",
      headers: { "content-type": "application/json", Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ companyId, items }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) return { ok: false, message: data?.error?.message ?? `AI eşleştirme hatası (HTTP ${response.status}).` };
    if (!Array.isArray(data?.matches) || typeof data?.model !== "string") {
      return { ok: false, message: "AI eşleştirme yanıtı beklenen biçimde değil." };
    }
    return { ok: true, model: data.model, matches: data.matches, unresolved: Array.isArray(data.unresolved) ? data.unresolved : [] };
  } catch {
    return { ok: false, message: "AI eşleştirme servisine bağlantı kurulamadı." };
  }
}
